<template>
  <v-dialog v-model="showDialog" persistent>
    <v-card outlined class="pa-3" v-if="loaded">
      <v-col class="text-right px-4">
        <v-btn icon @click="closeDialog">
          <v-icon>close</v-icon>
        </v-btn>
      </v-col>

      <v-card-title>
        <h6 class="mb-0 font-weight-bold">{{ mergePreview.users.username}}</h6>
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <!-- Left side -->
          <v-col>
            <LabelField
                :label="$t('settings/settings:duplicate.preview.users.labels.username')"
                :value="mergePreview.users.username"
                flex
            />

            <LabelField
                :label="$t('common:name')"
                :value="mergePreview.users.name"
                flex
            />

            <LabelField
                :label="$t('common:email')"
                :value="mergePreview.users.email"
                flex
            />

            <LabelField
                :label="$t('common:phone')"
                :value="mergePreview.users.phone ? mergePreview.users.phone : '-'"
                flex
            />

            <LabelField
                :label="$t('settings/settings:duplicate.preview.users.labels.user-group')"
                :value="mergePreview.users.translatedBookHierarchy"
                flex
            />

            <LabelField
                :label="$t('common:customer')"
                :value="mergePreview.users.customerName"
                flex
            />

            <LabelField
                :label="$t('settings/settings:duplicate.preview.users.labels.language')"
                :value="mergePreview.users.translatedLanguage"
                flex
            />

            <LabelField
                :label="$t('common:created_at')"
                :value="mergePreview.users.createdAt"
                flex
            />

            <LabelField
                :label="$t('common:last-updated-on')"
                :value="mergePreview.users.updatedAt"
                flex
            />
          </v-col>

          <v-col>
            <v-checkbox
                :label="$t('common:active')"
                v-model="mergePreview.users.active"
                disabled
                hide-details
            />

            <v-checkbox
                :label="$t('settings/settings:duplicate.preview.users.labels.main')"
                v-model="mergePreview.users.main"
                disabled
                hide-details
            />

            <v-checkbox
                :label="$t('settings/settings:duplicate.preview.users.labels.pending-registration')"
                v-model="mergePreview.users.registrationPending"
                disabled
                hide-details
            />

            <v-checkbox
                :label="$t('settings/settings:duplicate.preview.users.labels.mail-confirmation')"
                v-model="mergePreview.users.mailConfirmation"
                disabled
                hide-details
            />

            <v-checkbox
                :label="$t('settings/settings:duplicate.preview.users.labels.mail-notification')"
                v-model="mergePreview.users.mailNotification"
                disabled
                hide-details
            />

            <v-checkbox
                :label="$t('settings/settings:duplicate.preview.users.labels.hide-warning')"
                v-model="mergePreview.users.hideWarning"
                disabled
                hide-details
            />
          </v-col>
        </v-row>
        <h6 class="mb-0 font-weight-bold">{{ $t('common:documentation_plural') }}</h6>
        <DataTable
            :headers="documentationTable"
            :data="mergePreview.documentations"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewDocumentation"
            sortBy="name"
        />

        <BaseButton
            :btn-text="$t('settings/settings:duplicate.preview.confirm-merge')"
            color="warning"
            @clickedButton="$refs.warningDialog.openDialog()"
        />
      </v-card-text>
    </v-card>

    <MergeWarningDialog
        ref="warningDialog"
        :objects-to-remove-names="secondaryUserNames"
        :object-translation="$t('common:user_plural')"
        @confirmMerge="confirmMerge"
    />

    <DocumentViewer ref="documentViewer"/>
  </v-dialog>
</template>

<script>
import DataTable from "@/modules/core/components/DataTable/DataTable";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import MergeWarningDialog from "@/modules/settings/views/settings/duplicateMerge/MergeWarningDialog";
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";
import Utils from "@/modules/core/services/utils";
import LabelField from "@/modules/core/components/LabelField";
import DocumentViewer from "@/modules/core/components/DocumentViewer/DocumentViewer";
export default {
  name: "UsersMerging",
  components: {DocumentViewer, LabelField, MergeWarningDialog, BaseButton, DataTable},
  data() {
    return {
      mergePreview: {},
      loaded: false,
      mainUserId: null,
      secondaryUserIds: [],
      secondaryUserNames: [],
      showDialog: false
    }
  },
  computed: {
    documentationTable() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:role'), value: 'roleName'},
        {text: this.$t('common:valid-from'), value: 'startDate'},
        {text: this.$t('common:valid-until'), value: 'endDate'},
        {text: "", value: 'controls'}
      ]
    }
  },
  methods: {
    closeDialog() {
      this.mergePreview = {};
      this.loaded = false;
      this.mainUserId = null;
      this.secondaryUserIds = [];
      this.secondaryUserNames = [];
      this.showDialog = false;
    },

    createPreview(duplicateGroup) {
      this.showDialog = true;

      let userIds = [];
      for (let user of duplicateGroup.objects) {
        if (duplicateGroup.primaryObjectId !== user.id) {
          userIds.push(user.id);
          this.secondaryUserIds.push(user.id);
          this.secondaryUserNames.push(user.username);
        }
      }

      this.mainUserId = duplicateGroup.primaryObjectId;

      DuplicateCheckService.readUsersMergePreview(duplicateGroup.primaryObjectId, userIds,
          (res) => {
            this.mergePreview = res.data;
            this.mergePreview.users.translatedBookHierarchy = this.mergePreview.users.bookHierarchy ? this.$t('enums:hierarchy.' + this.mergePreview.users.bookHierarchy) : '-';
            this.mergePreview.users.translatedLanguage = this.mergePreview.users.language ? this.$t('enums:languages.' + this.mergePreview.users.language.toLowerCase()) : '-';
            this.loaded = true;
          },
          (error) => {
            this.$logger.error("Failed reading user merge preview, because: " + error);
            Utils.showFailureSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.failed-reading-preview'))
          }
      )
    },

    confirmMerge() {
      this.$refs.warningDialog.closeDialog();
      DuplicateCheckService.mergeUsers(this.mainUserId, this.secondaryUserIds,
          () => {
            Utils.showSuccessSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.merge-successful', { name: this.$t('common:user_plural')}));
            this.$emit('finishedMerge', this.secondaryUserIds);
            this.closeDialog();
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.merge-failed', { name: this.$t('common:user_plural')}));
            this.$logger.error("Failed merging users because: " + error);
          }
      )
    },

    viewDocumentation(data) {
      this.$refs.documentViewer.initViewer(this.mergePreview.documentations, data.fileName);
    },
  }
}
</script>

<style scoped>

</style>